/**
 * AnalyticsEngine.js
 */
import React, { useState } from 'react';
import { db } from "../firebase";
import { doc, updateDoc, increment } from "firebase/firestore";

// ─── HILFSFUNKTIONEN ─────────────────────────────────────────

// Summiert dailyStats über ein Array von Datums-Strings
export const sumDailyStats = (track, dates, field) => {
  if (!track.dailyStats) return 0;
  return dates.reduce((sum, date) => sum + (track.dailyStats[date]?.[field] || 0), 0);
};

// Generiert Array von Datums-Strings für die letzten N Tage
export const getDateRange = (days) => {
  const dates = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    dates.push(d.toISOString().split('T')[0]);
  }
  return dates;
};

// --- 1. GLOBALE STATS ---

export const getGlobalStats = (tracks) => {
  if (!tracks || tracks.length === 0) return {
    totalTracks: 0, totalPlays: 0, totalLikes: 0, totalUsers: 0,
    totalPlaytimeMin: 0, totalMB: 0, totalInteractions: 0,
    totalArtists: 0, newTracksThisWeek: 0 
  };

  const stats = tracks.reduce((acc, track) => {
    acc.totalPlays += (track.plays || 0);
    acc.totalPlaytime += (track.totalPlaytimeSec || 0);
    acc.totalMB += (track.fileSizeMB || 0);
    if (track.likes && Array.isArray(track.likes)) {
      acc.totalLikes += track.likes.length;
      track.likes.forEach(id => acc.allInteractingUsers.add(id));
    }
    if (track.userId) acc.allInteractingUsers.add(track.userId);
    return acc;
  }, { totalPlays: 0, totalLikes: 0, totalPlaytime: 0, allInteractingUsers: new Set(), totalMB: 0 });

  const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;

  return {
    totalTracks: tracks.length,
    totalPlays: stats.totalPlays,
    totalLikes: stats.totalLikes,
    totalUsers: stats.allInteractingUsers.size,
    totalPlaytimeMin: Math.floor(stats.totalPlaytime / 60),
    totalMB: stats.totalMB.toFixed(2),
    totalInteractions: stats.totalPlays + stats.totalLikes,
    totalArtists: new Set(tracks.map(t => t.userId)).size,
    newTracksThisWeek: tracks.filter(t => 
      t.createdAt?.seconds * 1000 > oneWeekAgo
    ).length,
  };
};

export const formatAirtime = (totalMinutes) => {
  if (!totalMinutes) return "0 MIN";
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const mins = totalMinutes % 60;
  if (days > 0) return `${days}T ${hours}H ${mins}MIN`;
  if (hours > 0) return `${hours}H ${mins}MIN`;
  return `${mins}MIN`;
};

export const getTrendingTracks = (tracks, limit = 5) => {
  return [...tracks]
    .sort((a, b) => {
      const scoreA = (a.plays || 0) + (a.likes?.length || 0) * 5;
      const scoreB = (b.plays || 0) + (b.likes?.length || 0) * 5;
      return scoreB - scoreA;
    })
    .slice(0, limit);
};

// --- 2. BESTEHENDER TRACK SCORE (unverändert, steuert onAir) ---

export const calculateTrackScore = (track) => {
  const likes = track.likes?.length || 0;
  const dislikes = track.dislikes?.length || 0;
  const plays = track.plays || 0;
  const skipCount = track.skipCount || 0;
  const completions = track.completions || 0;

  
  const ageDays = track.createdAt?.seconds 
    ? (Date.now() / 1000 - track.createdAt.seconds) / 86400 
    : 999;
  const freshBonus = Math.max(0, 4 - ageDays) * 2;

  const score = (likes * 4) - (dislikes * 4) + (plays * 0.05) - (skipCount * 3) + (completions * 2) + freshBonus;
  return Math.round(score * 10) / 10;
};

// --- 3. NEUER QUALITY SCORE (parallel, nur für Monitoring) ---
// Berechnet einen umfassenderen Score basierend auf Behavioral Analytics
// NICHT für Auto-OnAir verwendet – nur für Dashboard-Monitoring

export const calculateQualityScore = (track, periodDates = null) => {
  // Plays im Zeitraum oder gesamt
  const plays = periodDates
    ? sumDailyStats(track, periodDates, 'plays') || track.plays || 0
    : track.plays || 0;

  // Completions im Zeitraum oder gesamt
  const completions = periodDates
    ? sumDailyStats(track, periodDates, 'completions') || track.completions || 0
    : track.completions || 0;

  // Completion Rate (0-1)
  const completionRate = plays > 0 ? completions / plays : 0;

  const likes = track.likes?.length || 0;
  const skips = periodDates
    ? sumDailyStats(track, periodDates, 'skips') || track.skipCount || 0
    : track.skipCount || 0;

  // Shares haben wir noch nicht getrackt → Placeholder 0
  const shares = track.shares || 0;

  // Quality Score Formula:
  // Plays × 1 + CompletionRate% × 10 + Likes × 20 + Shares × 50 - Skips × 5
  const qScore = Math.round(
    (plays * 1) +
    (completionRate * 100 * 10) +
    (likes * 20) +
    (shares * 50) -
    (skips * 5)
  );

  return Math.max(0, qScore); // Nie negativ
};

// Quality Score Label für UI
export const getQualityLabel = (qScore) => {
  if (qScore >= 500) return { label: "🔥 HIT", color: "#00acc1" };
  if (qScore >= 200) return { label: "⭐ STARK", color: "#00acc1" };
  if (qScore >= 100) return { label: "✅ GUT", color: "#888" };
  if (qScore >= 50)  return { label: "📈 WÄCHST", color: "#888" };
  if (qScore >= 10)  return { label: "🌱 NEU", color: "#555" };
  return { label: "💤 SCHLÄFT", color: "#333" };
};

// --- 4. TIME-OF-DAY PATTERN ANALYSE ---

export const getTimePattern = (track) => {
  if (!track.playsByHour || Object.keys(track.playsByHour).length === 0) return null;

  const totalPlays = Object.values(track.playsByHour).reduce((s, v) => s + v, 0);
  if (totalPlays < 5) return null; // Zu wenig Daten

  // Tageszeiten-Gruppen
  const groups = {
    night:   [22, 23, 0, 1, 2, 3],        // 22:00 – 04:00
    morning: [5, 6, 7, 8, 9, 10],         // 05:00 – 11:00
    midday:  [11, 12, 13, 14, 15],        // 11:00 – 16:00
    evening: [16, 17, 18, 19, 20, 21],   // 16:00 – 22:00
  };

  const groupPlays = {};
  Object.entries(groups).forEach(([name, hours]) => {
    groupPlays[name] = hours.reduce((s, h) => s + (track.playsByHour[h.toString()] || 0), 0);
  });

  // Peak Gruppe
  const peak = Object.entries(groupPlays).sort((a, b) => b[1] - a[1])[0];
  const peakPct = Math.round((peak[1] / totalPlays) * 100);

  const labels = {
    night:   { label: "🌙 LATE NIGHT", color: "#6c63ff" },
    morning: { label: "🌅 MORNING",    color: "#ff9800" },
    midday:  { label: "☀️ MIDDAY",     color: "#ffcc00" },
    evening: { label: "🌆 EVENING",    color: "#00acc1" },
  };

  // Peak Stunde
  const peakHourEntry = Object.entries(track.playsByHour)
    .sort((a, b) => b[1] - a[1])[0];

  return {
    pattern: peak[0],
    ...labels[peak[0]],
    peakPct,
    peakHour: peakHourEntry ? parseInt(peakHourEntry[0]) : null,
    groupPlays,
    totalPlays,
  };
};

// --- 5. SCORE ENTWICKLUNG (für Monitoring) ---
// Vergleicht Quality Score über zwei Zeiträume

export const getScoreTrend = (track, currentDates, prevDates) => {
  const currentQScore = calculateQualityScore(track, currentDates);
  const prevQScore = calculateQualityScore(track, prevDates);

  if (prevQScore === 0 && currentQScore === 0) return { trend: "flat", delta: 0 };
  if (prevQScore === 0) return { trend: "up", delta: currentQScore };

  const delta = currentQScore - prevQScore;
  const pct = Math.round((delta / prevQScore) * 100);

  return {
    trend: delta > 10 ? "up" : delta < -10 ? "down" : "flat",
    delta,
    pct,
    current: currentQScore,
    previous: prevQScore,
  };
};

// --- 6. UI KOMPONENTE (unverändert) ---

export const AnalyticsDashboard = ({ tracks, currentTrackIndex, isSecret = false }) => {
  const [isVisible, setIsVisible] = useState(!isSecret);
  const [clickCount, setClickCount] = useState(0);
  const [isPressed, setIsPressed] = useState(false);

  const globalStats = getGlobalStats(tracks);
  const currentTrack = tracks && tracks.length > 0 ? tracks[currentTrackIndex] : null;

  const handleToggle = async () => {
    setIsPressed(true);
    setTimeout(() => setIsPressed(false), 100);

    try {
      const globalRef = doc(db, "stats", "global");
      await updateDoc(globalRef, { totalClicks: increment(1) });
    } catch (e) { 
      console.warn("Analytics Sync Error."); 
    }

    if (!isSecret) return;

    setClickCount(prev => {
      const newCount = prev + 1;
      if (newCount === 3) {
        setIsVisible(!isVisible);
        return 0;
      }
      return newCount;
    });

    setTimeout(() => setClickCount(0), 2000);
  };

  const labelStyle = { color: "#555" }; 
  const valueStyle = { color: "#eee" };

  return (
    <div 
      onClick={handleToggle} 
      style={{ 
        width: "100%", cursor: "pointer",
        userSelect: "none", WebkitUserSelect: "none",
        transition: "transform 0.1s ease, opacity 0.1s ease",
        opacity: isPressed ? 0.4 : 1,
        transform: isPressed ? "scale(0.98)" : "scale(1)",
        marginTop: "15px"
      }}
    >
      {isSecret && !isVisible && (
        <div style={{ height: "60px", display: "flex", justifyContent: "center", alignItems: "center" }}>
          <div style={{ width: "4px", height: "4px", borderRadius: "50%", background: "rgba(255,255,255,0.05)" }} />
        </div>
      )}

      {isVisible && (
        <div style={{ 
          padding: "15px 5px", fontFamily: "inherit",
          fontSize: "0.65rem", fontWeight: "900",
          display: "flex", justifyContent: "space-between", alignItems: "flex-end",
          borderTop: isSecret ? "1px solid rgba(0,172,193,0.2)" : "1px solid rgba(255,255,255,0.05)",
          letterSpacing: "1px"
        }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", textAlign: "left" }}>
            <div><span style={labelStyle}>TRACKS PLAYED </span><strong style={valueStyle}>{globalStats.totalPlays}</strong></div>
            <div><span style={labelStyle}>AIRTIME </span><strong style={valueStyle}>{globalStats.totalPlaytimeMin} MIN</strong></div>
            <div><span style={labelStyle}>AUDIENCE </span><strong style={valueStyle}>{globalStats.totalUsers}</strong></div>
            <div><span style={labelStyle}>TOTAL LOVE </span><strong style={valueStyle}>{globalStats.totalLikes}</strong></div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ color: "#00acc1", fontSize: "0.6rem", fontWeight: "900", marginBottom: "5px", letterSpacing: "2px" }}>CURRENT</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <div style={{ color: "#eee", fontWeight: "900" }}><span style={{ color: "#00acc1", marginRight: "5px" }}>🎧</span>{currentTrack?.plays || 0}</div>
              <div style={{ color: "#eee", fontWeight: "900" }}><span style={{ color: "#00acc1", marginRight: "5px" }}>♥</span>{currentTrack?.likes?.length || 0}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- 7. KOMPATIBILITÄTS-LAYER ---
export const AnalyticsEngine = {
  getGlobalStats,
  getTrendingTracks
};